import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useState } from "react";

import { slackHook } from "src/utils/slack-hook";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useAuth } from "src/hooks/use-auth";
import { SeverityPillWrap } from "src/components/severity-pill-wrap";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import toast from "react-hot-toast";

interface StripeProps {
  handlePaymentSuccess: (payment_method_id?: string) => void;
  paymentRequired: boolean;
  currentPaymentIntent: any; // Add this prop to access the payment intent data
  onCancel?: () => void;
}

export function SnugStripe(props: StripeProps) {
  const { handlePaymentSuccess, paymentRequired, currentPaymentIntent, onCancel } = props;
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const [attemptingPayment, setAttemptingPayment] = useState<boolean>();
  const [error, setError] = useState<string>();
  const [buttonText, setButtonText] = useState<string>("Pay and continue");

  const handleSubmit = async () => {
    setError("");
    if (!stripe || !elements) {
      setError(
        `Oh no! We're having an issue loading some data. Wait a few moments then give it another go.`
      );
      return;
    }

    setAttemptingPayment(true);
    setButtonText("Processing...");

    const returnUrl = window.location.href;

    const { paymentIntent, error } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: "if_required",
    });

    if (error) {
      setError(error.message || "Payment failed. Please check your payment details and try again.");
      slackHook(`Payment Error: ${error.message}`);
      setButtonText("Pay and finalize documents");
      setAttemptingPayment(false);
      return;
    }

    const status = paymentIntent?.status;
    // Extract payment method ID as string (handle both string and PaymentMethod object cases)
    const paymentMethodId = typeof paymentIntent?.payment_method === 'string'
      ? paymentIntent.payment_method
      : (paymentIntent?.payment_method as any)?.id || undefined;

    switch (status) {
      case "succeeded":
        // Get application fee from payment intent if available
        const applicationFee = (paymentIntent as any)?.application_fee_amount ||
                               currentPaymentIntent?.application_fee_amount;

        if (applicationFee) {
          toast.success(
            `Payment successful!`,
            { duration: 6000 }
          );

          // Also log this to Slack for transparency
          slackHook(
            `${window.location.hostname}: PAYMENT SUCCESS for ${user?.username}. Amount: ${paymentIntent?.amount}, Application Fee: ${applicationFee}`
          );
        } else {
          toast.success("Payment successful!");
          slackHook(
            `${window.location.hostname}: PAYMENT SUCCESS for ${user?.username}. Amount: ${paymentIntent?.amount}. Check stripe dashboard for application fee.`
          );
        }

        setButtonText("Success!");
        handlePaymentSuccess(paymentMethodId);
        break;

      case "processing":
        // Async payment processing - show success, webhook will finalize
        toast.success("Payment is being processed. You'll be notified when complete.", { duration: 6000 });
        slackHook(`${window.location.hostname}: PAYMENT PROCESSING for ${user?.username}. Amount: ${paymentIntent?.amount}`);
        setButtonText("Processing...");
        handlePaymentSuccess(paymentMethodId); // Let webhook handle final state
        break;

      case "requires_action":
        // Fallback for complex 3DS flows that couldn't be handled inline
        setButtonText("Authenticating...");
        const { error: actionError } = await stripe.confirmPayment({
          elements,
          confirmParams: { return_url: returnUrl },
          redirect: "always",
        });
        if (actionError) {
          setError("Authentication required. Please use a different card or try again.");
          slackHook(`Payment Authentication Failed: ${actionError.message}`);
          setAttemptingPayment(false);
          setButtonText("Pay and finalize documents");
        }
        break;

      case "requires_payment_method":
        setError("Please check your card details and try again.");
        slackHook(`Payment Requires New Method: ${paymentIntent?.last_payment_error?.message || 'Unknown error'}`);
        setAttemptingPayment(false);
        setButtonText("Pay and finalize documents");
        break;

      case "requires_capture":
        // Manual capture required - treat as success for now
        toast.success("Payment authorized successfully!");
        slackHook(`${window.location.hostname}: PAYMENT AUTHORIZED for ${user?.username}. Amount: ${paymentIntent?.amount}`);
        setButtonText("Authorized!");
        handlePaymentSuccess(paymentMethodId);
        break;

      default:
        // Fallback for other statuses - let webhook handle
        toast.success("Payment submitted. Processing...", { duration: 6000 });
        slackHook(`${window.location.hostname}: PAYMENT STATUS ${status} for ${user?.username}. Amount: ${paymentIntent?.amount}`);
        setButtonText("Processing...");
        handlePaymentSuccess(paymentMethodId);
        break;
    }
  };

  return (
    <>
      <Box sx={{ pb: 0 }}>
        <form>
          {(!paymentRequired) ? (
            <SeverityPillWrap color="success" sx={{ width: '100%' }}>
              Your payment was successful!
            </SeverityPillWrap>
          ) : (
            <>
              <PaymentElement
                options={{
                  layout: {
                    type: 'accordion',
                    defaultCollapsed: false,
                    radios: false,
                    spacedAccordionItems: true,
                  },
                }}
              />
              <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  sx={{borderRadius:1}}
                  color={(!paymentRequired) ? "success" : "primary"}
                  disabled={!stripe || attemptingPayment}
                  onClick={(e) => handleSubmit()}
                  startIcon={<LockRoundedIcon />}
                >
                  {buttonText}
                </Button>
                {onCancel && (
                  <Button
                    variant="text"
                    color='inherit'
                    onClick={onCancel}
                    disabled={attemptingPayment}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
            </>
          )}
        </form>
      </Box>
      {error && <p style={{ color: "red" }}>{error}</p>}
    </>
  );
}
